'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductCardProps } from '@eliadmin/core/src/components/product/product-card';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import type { CarouselProps } from '@eliadmin/core/src/components/ui/carousel';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { watchHorizontalProductResize } from '../../utils/product-carousel-resize';

export type ProductCarouselProps = Omit<CarouselProps, 'slides' | 'className'> & {
  products: Product[];
  deferOffscreenImages?: boolean;
  className?: {
    carousel?: CarouselProps['className'];
    product?: ProductCardProps['className'];
  };
};

// Match the slide bases below: the initial 1/2/3/4 visible images remain native.
export function getDeferredProductImageMedia(index: number) {
  if (index === 0) return undefined;
  if (index === 1) return '(scripting: enabled) and (max-width: 639px)';
  if (index === 2) return '(scripting: enabled) and (max-width: 767px)';
  if (index === 3) return '(scripting: enabled) and (max-width: 1023px)';
  return '(scripting: enabled)';
}

export function ProductCarousel({
  className,
  products,
  deferOffscreenImages,
  ...props
}: ProductCarouselProps) {
  const currency = useGlobalStore(s => s.generalLayout?.currency);
  return (
    <>
      <ProductListSchema products={products} currency={currency} />
      <Carousel
        className={{
          ...(className?.carousel as object),
          wrapper: cn('ea-product-carousel', className?.carousel?.wrapper),
          main: cn('w-full', className?.carousel?.main),
          container: cn('min-w-full', className?.carousel?.container),
          // Responsive: <sm: 1, <md: 2, <lg: 3, lg+: 4
          slide: cn(
            'w-full basis-full p-2 sm:basis-1/2 md:basis-1/3 lg:basis-1/4',
            className?.carousel?.slide,
          ),
        }}
        slides={products.map((product, i) => (
          <ProductCard
            key={`${product.id}-${i}`}
            product={product}
            className={className?.product}
            deferImageMedia={deferOffscreenImages ? getDeferredProductImageMedia(i) : undefined}
          />
        ))}
        showArrows={false}
        enableLoop
        emblaRef={props.emblaRef}
        options={{
          align: 'start',
          loop: true,
          slidesToScroll: 1,
          watchResize: watchHorizontalProductResize,
        }}
      />
    </>
  );
}
